import * as React from 'react';
import { useCallback, 
         useEffect, 
         useMemo 
       }                                    from 'react';
import { useSelector, 
         useDispatch 
       }                                    from 'react-redux';
import { DataGridPro, 
         GridColDef,
         DataGridProProps,
         useGridApiRef,
         GridActionsCellItem,
         GridRenderEditCellParams,
         useGridApiContext
    }                                       from '@mui/x-data-grid-pro';
import { Box, 
         Tooltip, 
         Typography,
         Select,
         MenuItem,
         FormControl,
         Card,
         CardContent
       }                                    from '@mui/material';
import { useGlobalConfig }                  from 'context/GlobalContext';
import { HomeProfileAPI }                   from 'apis/homeProfileAPI';
import { selectHomeProfileMetadata, 
         selectHomeProfile, 
         setHomeProfile,
         updateHomeProfile,
         selectUnsavedRows,
         setUnsavedRowAction,
         discardUnsavedRowAction,
         discardAllUnsavedRowsAction,
         setHomeProfileMetadata
       }                                    from 'store/dataReducers/homeProfileSlice';
import { selectCMSSelectedPilotDocId }      from 'store/dataReducers/cmsPilotSlice';
import { ApiErrorInfo }                     from 'types';
import { BglCloseOutlinedSmallButton, 
         BglPrimaryButton,  
         BglPrimaryOutlinedSmallButton 
       }                                    from 'components/button/BglButtons';

import VisibilityOffIcon                    from '@mui/icons-material/VisibilityOff';
import VisibilityIcon                       from '@mui/icons-material/Visibility';
import SaveIcon                             from '@mui/icons-material/Save';
import RestoreIcon                          from '@mui/icons-material/Restore';
import { SelectChangeEvent }                from '@mui/material/Select';
import { isEqual }                          from 'lodash';
import { useTheme }                         from '@mui/material/styles';
import { getHomeProfilePageStyles }         from './CMSHomeProfilePageStyles';
import { BglLoading }                       from 'components/loading';

// Custom edit cell for defaultValue dropdown
const DefaultValueEditCell = (props: GridRenderEditCellParams) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const metadata = useSelector(selectHomeProfileMetadata)!;
  
  const [selectedValue, setSelectedValue] = React.useState<string>(value || '');
  
  // Find the attribute to get its available values
  const attributeId = id.toString().split('-')[1]; // Extract attributeId from id like "attribute-attributeId"
  const attribute = metadata.find(attr => attr.attributeId === attributeId);
  
  if (!attribute || !attribute.values) return null;
  
  // Get dropdown options from attribute values - always use tag values
  const values = attribute.values as any[];
  const dropdownOptions = values.map((val: any) => val.tag);
  
  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };
  
  return (
    <FormControl fullWidth size="small">
      <Select
        value={selectedValue}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '22px',
          padding: '8px',
          borderRadius: '4px'
        }}
      >
        {dropdownOptions.map((option: string) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface TransformedHomeProfileData {
  id: string;
  path: string[];
  name: string;
  accountType: string;
  attributeId?: string;
  valueId?: string;
  valueName?: string;
  valueTag?: string;
  defaultValue?: string;
  disabled?: boolean;
  isAttribute?: boolean;
  isValue?: boolean;
}

export default function CMSHomeProfilePage() {

  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const styles = getHomeProfilePageStyles(theme);
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const [isLoading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<ApiErrorInfo | undefined>(undefined);
  const [isSaving, setIsSaving] = React.useState(false);
  
  const metadata = useSelector(selectHomeProfileMetadata);
  const homeProfile = useSelector(selectHomeProfile)!;
  const utilityDocId = useSelector(selectCMSSelectedPilotDocId);
  const unsavedRows = useSelector(selectUnsavedRows);

  // Check if user has permission to update home profile attributes
  const canUpdate = rc.permissionsManager.canUtilityHomeProfileUpdate();

  const getHomeProfile = useCallback(async () => {
    
    setLoading(true);

    const resp = await HomeProfileAPI.getPilotHomeProfile(rc, utilityDocId);
    if (resp.error) {
      setError(resp.error);
    } else {
      dispatch(setHomeProfile(resp.data));
      setError(undefined);
    }
    setLoading(false);
  }, [rc, utilityDocId, dispatch]);

  const getHomeProfileMetadata = useCallback(async () => {
    setLoading(true);

    const resp = await HomeProfileAPI.getHomeProfileAttributes(rc);
    if (resp.error) {
      setError(resp.error);
    } else {
      dispatch(setHomeProfileMetadata(resp.data));
      setError(undefined);
    }
    setLoading(false);
  }, [rc, dispatch]);

  const saveHomeProfileChanges = useCallback(async (attributeId: string, changes: any) => {
    setIsSaving(true);
    
    // Prepare the update with only changed data
    const updatedProfile: any = {};
    
    // Convert tag value back to id value for API
    if (changes.defaultValue !== undefined) {
      const attribute = metadata!.find(attr => attr.attributeId === attributeId)!;
      const values = attribute.values as any[];
      const matchingValue = values.find(val => val.tag === changes.defaultValue);
      updatedProfile.defaultValue = {
        [attributeId]: matchingValue.id
      };
    }
    
    if (changes.disabled !== undefined) {
      updatedProfile.disabled = {
        [attributeId]: changes.disabled
      };
    }
    
    const resp = await HomeProfileAPI.updatePilotHomeProfile(rc, utilityDocId, updatedProfile);
    
    if (resp.error) {
      setError(resp.error);
    } else {
      dispatch(updateHomeProfile(resp.data));
      dispatch(discardUnsavedRowAction(attributeId));
    }
    
    setIsSaving(false);
  }, [rc, utilityDocId, metadata, dispatch]);

  const discardHomeProfileChanges = useCallback((attributeId: string) => {
    dispatch(discardUnsavedRowAction(attributeId));    
  }, [dispatch]);

  const discardAllUnsavedChanges = useCallback(() => {
    // Discard all unsaved changes
    dispatch(discardAllUnsavedRowsAction());
  }, [dispatch]);

  const saveAllUnsavedChanges = useCallback(async () => {
    setIsSaving(true);
    
    try {
      // Save all unsaved changes
      const unsavedEntries = Object.entries(unsavedRows);
      
      const updatedProfile: any = {
        defaultValue: {},
        disabled: {}
      };

      for (const [attributeId, changes] of unsavedEntries) {
        if (changes.defaultValue !== undefined) {
          const attribute = metadata!.find(attr => attr.attributeId === attributeId)!;
          const values = attribute.values as any[];
          const matchingValue = values.find(val => val.tag === changes.defaultValue);
          updatedProfile.defaultValue = {
            ...updatedProfile.defaultValue,
            [attributeId]: matchingValue.id
          };
        }
        if (changes.disabled !== undefined) {
          updatedProfile.disabled = {
            ...updatedProfile.disabled,
            [attributeId]: changes.disabled
          };
        }
      }

      const resp = await HomeProfileAPI.updatePilotHomeProfile(rc, utilityDocId, updatedProfile);

      if (resp.error) {
        setError(resp.error);
      } else {
        dispatch(updateHomeProfile(resp.data));
        dispatch(discardAllUnsavedRowsAction());
      }
    } catch (error) {
      console.error('Error saving all changes:', error);
    } finally {
      setIsSaving(false);
    }
  }, [unsavedRows, saveHomeProfileChanges]);

  // Store data in Redux when API response is received
  useEffect(() => {

    if (!homeProfile) getHomeProfile();
    if (!metadata) getHomeProfileMetadata();
    
    setLoading(false);
  }, []);

  // Transform data into tree structure for DataGridPro (original data without unsaved changes)
  const originalTransformedData = useMemo(() => {
    if (!metadata || metadata.length === 0 || !homeProfile) return [];

    const result: TransformedHomeProfileData[] = [];
    const defaultValue = homeProfile.defaultValue as Record<string, string> || {};
    const disabled = homeProfile.disabled as Record<string, boolean> || {};

    metadata.forEach((attribute: any) => {
      const isAttributeDisabled = disabled[attribute.attributeId] || false;
      
      // Add attribute row
      const currentDefaultValue = defaultValue[attribute.attributeId] || '';
      
      // Find the corresponding tag value for the current default value (which is stored as id)
      let tagValue = '';
      if (currentDefaultValue && attribute.values) {
        const values = attribute.values as any[];
        const matchingValue = values.find(val => val.id === currentDefaultValue);
        tagValue = matchingValue ? matchingValue.tag : '';
      }
      
      result.push({
        id: `attribute-${attribute.attributeId}`,
        path: [attribute.label || 'Unknown'],
        name: attribute.label || 'Unknown',
        accountType: Array.isArray(attribute.accountType) ? attribute.accountType.join(', ') : '',
        attributeId: attribute.attributeId,
        defaultValue: tagValue,
        disabled: isAttributeDisabled,
        isAttribute: true
      });

      // Add value rows as children (always show values, regardless of disabled state)
      if (attribute.values) {
        const values = attribute.values as any[];
        values.forEach((value: any) => {
          result.push({
            id: `value-${attribute.attributeId}-${value.id}`,
            path: [attribute.label || 'Unknown', value.name || 'Unknown'],
            name: '',
            accountType: '',
            attributeId: attribute.attributeId,
            valueId: value.id,
            valueName: value.name,
            valueTag: value.tag,
            isValue: true
          });
        });
      }
    });

    return result;
  }, [metadata, homeProfile]);

  // Transform data with unsaved changes applied for display
  const transformedData = useMemo(() => {
    if (!originalTransformedData.length) return [];

    return originalTransformedData.map(row => {
      if (row.isAttribute && row.attributeId) {
        const unsavedChanges = unsavedRows[row.attributeId];
        if (unsavedChanges) {
          return {
            ...row,
            defaultValue: unsavedChanges.defaultValue !== undefined ? unsavedChanges.defaultValue : row.defaultValue,
            disabled: unsavedChanges.disabled !== undefined ? unsavedChanges.disabled : row.disabled
          };
        }
      }
      return row;
    });
  }, [originalTransformedData, unsavedRows]);

  const toggleAttributeDisabled = useCallback((attributeId: string, currentDisabled: boolean) => {
    // Find the original row to compare with
    const originalRow = originalTransformedData.find(row => row.attributeId === attributeId)!;
    const newDisabled = !currentDisabled;
    
    // Check if the new value matches the original
    if (isEqual(newDisabled, originalRow.disabled)) {
      // If it matches the original, discard any unsaved changes for this field
      const unsavedRow = unsavedRows[attributeId];
      if (unsavedRow) {
        // If there are other unsaved changes (like defaultValue), keep those
        const otherChanges: any = {};
        if (unsavedRow.defaultValue !== undefined) {
          otherChanges.defaultValue = unsavedRow.defaultValue;
        }
        
        if (Object.keys(otherChanges).length > 0) {
          dispatch(setUnsavedRowAction({ id: attributeId, diff: otherChanges }));
        } else {
          dispatch(discardUnsavedRowAction(attributeId));
        }
      }
    } else {
      // If it doesn't match, set the unsaved change
      const changes: any = {
        disabled: newDisabled
      };
      
      // Preserve any existing unsaved changes for other fields
      const unsavedRow = unsavedRows[attributeId];
      if (unsavedRow && unsavedRow.defaultValue !== undefined) {
        changes.defaultValue = unsavedRow.defaultValue;
      }
      
      dispatch(setUnsavedRowAction({ id: attributeId, diff: changes }));
    }
  }, [dispatch, originalTransformedData, unsavedRows]);

  const processRowUpdate = useCallback<NonNullable<DataGridProProps['processRowUpdate']>>(
    (newRow: any, oldRow: any) => {
      // Only track changes for attribute rows (not values)
      if (newRow.isValue) {
        return newRow;
      }
      
      // Find the original row from originalTransformedData (source of truth)
      const originalRow = originalTransformedData.find(row => row.attributeId === newRow.attributeId);
      if (!originalRow) {
        return newRow;
      }
      
      const attributeId = newRow.attributeId;
      const changes: any = {};
      let hasChanges = false;
      
      // Check for defaultValue changes
      if (!isEqual(newRow.defaultValue, originalRow.defaultValue)) {
        changes.defaultValue = newRow.defaultValue;
        hasChanges = true;
      }
      
      // Check for disabled changes
      if (!isEqual(newRow.disabled, originalRow.disabled)) {
        changes.disabled = newRow.disabled;
        hasChanges = true;
      }
      
      if (hasChanges) {
        dispatch(setUnsavedRowAction({ id: attributeId, diff: changes }));
      } else {
        dispatch(discardUnsavedRowAction(attributeId));
      }
      
      return newRow;
    },
    [dispatch, originalTransformedData]
  );

  const onProcessRowUpdateError = React.useCallback<NonNullable<DataGridProProps['onProcessRowUpdateError']>>(
    (error: any) => {
      console.error(error);
    },
    []
  );

  // Update unsaved rows when cells are edited
  useEffect(() => {
    // Get all attribute rows from originalTransformedData (only attribute rows, not value rows)
    const attributeRows = originalTransformedData.filter(row => row.attributeId && row.isAttribute);
    
    attributeRows.forEach((originalRow) => {
      const attributeId = originalRow.attributeId;
      if (attributeId) {
        const unsavedRow = unsavedRows[attributeId];
        
        if (unsavedRow) {
          // Apply unsaved changes only to attribute rows
          apiRef.current?.updateRows([{...originalRow, ...unsavedRow}]);
        } else {
          // Reset to original values (no unsaved changes)
          apiRef.current?.updateRows([originalRow]);
        }
      }
    });
  }, [apiRef, originalTransformedData, unsavedRows]);

  // Expand all tree nodes when data is loaded and keep them expanded
  useEffect(() => {
    if (transformedData.length > 0 && !isLoading) {
      // Expand all attribute rows (they all have children now)
      const attributeIds = transformedData
        .filter(row => row.isAttribute)
        .map(row => row.id);
      
      // Expand all attribute nodes
      attributeIds.forEach(id => {
        apiRef.current?.setRowChildrenExpansion(id, true);
      });
    }
  }, [transformedData, isLoading, apiRef, unsavedRows]);

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) => row.path;

  const getRowClassName: DataGridProProps['getRowClassName'] = (params) => {
    const row = transformedData.find(r => r.id === params.id);
    if (row?.isAttribute) {
      // Check if attribute row has unsaved changes
      const attributeId = row?.attributeId;
      if (attributeId && unsavedRows[attributeId]) {
        return 'attribute-row row--edited';
      }
      // Check if attribute is disabled
      if (row.disabled) {
        return 'attribute-row row--disabled';
      }
      return 'attribute-row';
    } else if (row?.isValue) {
      // Check if the parent attribute is disabled
      const parentAttribute = transformedData.find(r => 
        r.isAttribute && r.attributeId === row.attributeId
      );
      if (parentAttribute?.disabled) {
        return 'value-row row--disabled';
      }
      return 'value-row';
    } else {
      return 'default-row';
    }
  };

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: 'Home Profile Attributes',
    hideDescendantCount: true,
    width: 300,
  }

  const columns: GridColDef[] = [
    {
      field: 'accountType',
      headerName: 'Account Type',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2"
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center',
            color: 'inherit',
          }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'defaultValue',
      headerName: 'Default Profile',
      width: 400,
      editable: canUpdate,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value || ''}
        </Typography>
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <DefaultValueEditCell {...params} />
      )
    },
    {
      field: 'valueTag',
      headerName: 'Profile Tag',
      width: 400,
      renderCell: (params) => (
        <Typography variant="body2"
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center',
            color: 'inherit',
          }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'disabled',
      headerName: 'Disabled',
      width: 120,
      type: 'boolean',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value !== undefined ? (params.value ? 'Yes' : 'No') : ''}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      width: 240,
      headerName: 'Actions',
      align: 'left',
      headerAlign: 'left',
      getActions : ({id, row}) => {
        if (row.isAttribute) {
          // Attribute level actions - Disable/Enable and Save/Discard
          const attributeId = row.attributeId;
          const hasUnsavedChanges = unsavedRows[attributeId];
          
          return [
            <Tooltip title={row.disabled ? "Enable attribute" : "Disable attribute"}>
              <span>
                <GridActionsCellItem
                  icon={row.disabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  label={row.disabled ? "Enable attribute" : "Disable attribute"}
                  disabled={!canUpdate}
                  onClick={() => toggleAttributeDisabled(attributeId!, row.disabled!)}
                />
              </span>
            </Tooltip>,
            <Tooltip title="Discard changes">
              <span>
                <GridActionsCellItem
                  icon={<RestoreIcon />}
                  label="Discard changes"
                  disabled={!hasUnsavedChanges || !canUpdate}
                  onClick={() => discardHomeProfileChanges(attributeId!)}
                />
              </span>
            </Tooltip>,
            <Tooltip title="Save changes">
              <span>
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label="Save changes"
                  disabled={!hasUnsavedChanges || !canUpdate}
                  onClick={() => saveHomeProfileChanges(attributeId!, hasUnsavedChanges)}
                />
              </span>
            </Tooltip>
          ];
        }
        
        return [];
      }
    }
  ];

  if (error) {
    return (
      <Box sx={styles.errorContainer}>
        <Typography variant="h6" sx={{ 
          color: theme.palette.text.primary
        }}>{error.message}</Typography>
        <BglPrimaryButton
          onClick={() => getHomeProfile()}>
          Retry
        </BglPrimaryButton>
      </Box>
    );
  }

  return (
    <Box sx={styles.homeProfileContainer}>

    {
      isLoading ? 
      <Box sx={styles.loadingContainer}>
        <BglLoading />
      </Box> 
      
      : error ? 
      <Box sx={styles.errorContainer}>
        <Typography variant="h6" sx={{ 
          color: theme.palette.text.primary
        }}>{error}</Typography>
        <BglPrimaryButton
          onClick={() => getHomeProfile()}>
          Retry
        </BglPrimaryButton>
      </Box>

      : 
      <>

        {/* Guidelines */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 'bold',
                color: theme.palette.text.secondary
              }}>Guidelines</Typography>
              <Typography variant="body2" sx={{ 
                color: theme.palette.text.secondary
              }}>Here we configure the Home Profile for the Project indicating which Premise characteristics are considered for Exclusion (Recos) and for which characteristics we would take into account the definition of - </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary
                }}><b>Default Profile </b>: Value to consider when no User Survey response is available for this characteristic</Typography>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary
                }}><b>Disabled </b>: Disable the characteristic from being considered for Exclusion (Recos)</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Bulk action buttons */}
        <Box sx={{ mb: 1, display: 'flex', gap: 2 }}>
          <BglCloseOutlinedSmallButton
            disabled={isSaving || !canUpdate || Object.keys(unsavedRows).length === 0}
            onClick={discardAllUnsavedChanges}
            startIcon={<RestoreIcon />}>
            {`Discard all (${Object.keys(unsavedRows).length})`}
          </BglCloseOutlinedSmallButton>
          <BglPrimaryOutlinedSmallButton
            disabled={isSaving || !canUpdate || Object.keys(unsavedRows).length === 0}
            onClick={saveAllUnsavedChanges}
            startIcon={<SaveIcon />}>
            {`Save all (${Object.keys(unsavedRows).length})`}
          </BglPrimaryOutlinedSmallButton>
        </Box>

      <DataGridPro
        apiRef={apiRef}
        treeData
        getTreeDataPath={getTreeDataPath}
        getRowClassName={getRowClassName}
        groupingColDef={groupingColDef}
        rows={transformedData}
        columns={columns}
        loading={isLoading || isSaving}
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        sx={styles.dataGrid}
      />
      <br/>
      </>
      }

    </Box>
  );
}